/**
 * ADR-0031: Omni notifications. Each one is kept in the person's inbox and announced as
 * `omni.notification.created`. In the foreground the shell shows a banner; in the background
 * a system notification (a web browser too, if the person allowed it). With Omni closed, the
 * phone fetches them itself (native NotificationPoller), starting after the last one the
 * shell saw, so nothing shows twice. Events replayed after a long gap are skipped: they
 * stay in the inbox.
 */
import { registerPlugin } from "@capacitor/core";
import type { OmniEvent } from "@omni/sdk/protocol";

import { api } from "./api";
import { isNative } from "./platform";

export interface OmniNotice { id: number; notificationId: number | null; title: string; body: string; route: string }

export interface InboxItem {
  id: number;
  app_id: string | null;
  kind: string;
  title: string;
  body: string;
  link: string | null;
  data: Record<string, unknown>;
  created_at: string;
  read: boolean;
}

export const NOTIFICATION_EVENT = "omni.notification.created";
const PIPELINES_EVENT = "pipelines.notification.sent"; // older servers: pipelines only
const FRESH_MS = 10 * 60_000;

export function noticeFrom(e: OmniEvent): OmniNotice | null {
  if (Date.now() - new Date(e.created_at).getTime() > FRESH_MS) return null;
  const title = String(e.data.title ?? "").slice(0, 120);
  if (!title) return null;
  const body = String(e.data.body ?? "").slice(0, 500);
  if (e.type === NOTIFICATION_EVENT && !e.app_id) {
    const link = typeof e.data.link === "string" && e.data.link.startsWith("/") ? e.data.link : "/notifications";
    const nid = Number(e.data.notification_id) || null;
    return { id: e.id, notificationId: nid, title, body, route: link };
  }
  if (e.type === PIPELINES_EVENT && e.app_id === "pipelines" && !e.data.notification_id) {
    return { id: e.id, notificationId: null, title, body, route: "/apps/pipelines/activity" };
  }
  return null;
}

// ------------------------------------------------------------------ unread count

let unread = 0;
const unreadListeners = new Set<(n: number) => void>();

export function onUnread(fn: (n: number) => void): () => void {
  unreadListeners.add(fn);
  fn(unread);
  return () => unreadListeners.delete(fn);
}

export function setUnread(n: number): void {
  unread = Math.max(0, n);
  unreadListeners.forEach((fn) => fn(unread));
}

export async function refreshUnread(): Promise<number> {
  const r = await api<{ count: number }>("/api/notifications/unread");
  setUnread(r.count);
  return r.count;
}

export async function markRead(id: number): Promise<void> {
  await api(`/api/notifications/${id}/read`, { method: "POST" });
  void refreshUnread().catch(() => undefined);
}

// ------------------------------------------------------------------ phone

interface NativeSeen { notificationsSeen(o: { id: number }): Promise<void> }
const Native = registerPlugin<NativeSeen>("OmniCapture");

/** The phone's background check starts after this one (so it isn't shown twice). */
export async function seen(id: number): Promise<void> {
  if (isNative && id > 0) await Native.notificationsSeen({ id }).catch(() => undefined);
}

export type PhonePermission = "granted" | "denied" | "prompt" | "unavailable";

export async function notificationPermission(ask = false): Promise<PhonePermission> {
  if (isNative) {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    let perm = await LocalNotifications.checkPermissions();
    if (ask && (perm.display === "prompt" || perm.display === "prompt-with-rationale")) perm = await LocalNotifications.requestPermissions();
    return perm.display === "granted" ? "granted" : perm.display === "denied" ? "denied" : "prompt";
  }
  if (typeof Notification === "undefined") return "unavailable";
  let p = Notification.permission;
  if (ask && p === "default") p = await Notification.requestPermission();
  return p === "granted" ? "granted" : p === "denied" ? "denied" : "prompt";
}

/** A system notification, for when Omni isn't on screen. */
export async function systemNotify(n: OmniNotice): Promise<void> {
  if (isNative) {
    if ((await notificationPermission()) !== "granted") return;
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.schedule({
      notifications: [{ id: n.id % 2_147_483_647, title: n.title, body: n.body, extra: { route: n.route, notificationId: n.notificationId } }],
    });
    if (n.notificationId) await seen(n.notificationId);
    return;
  }
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    new Notification(n.title, { body: n.body, tag: `omni-${n.id}` });
  }
}

/** Tapping a system notification opens where it points (and marks it read). */
export async function onNotificationTap(fn: (route: string) => void): Promise<() => void> {
  if (!isNative) return () => {};
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const h = await LocalNotifications.addListener("localNotificationActionPerformed", (a) => {
    const extra = a.notification.extra as { route?: string; notificationId?: number } | undefined;
    if (extra?.notificationId) void markRead(extra.notificationId).catch(() => undefined);
    if (extra?.route?.startsWith("/")) fn(extra.route);
  });
  return () => void h.remove();
}
